import type {
  BrowserProvider,
  BrowserSettings,
  BrowserViewport,
  GlobalSettings,
  StagehandEnv,
} from '@mastra/code-sdk/onboarding/settings';
import {
  checkProfileProviderMismatch,
  createBrowserFromSettings,
  loadSettings,
  parseViewportInput,
  saveSettings,
  setProfileProvider,
  VIEWPORT_PRESETS,
} from '@mastra/code-sdk/onboarding/settings';
import type { MastraBrowser } from '@mastra/core/browser';
import { STAGEHAND_MODEL_PROVIDERS } from '@mastra/stagehand';
import type { ModelItem } from '../components/model-selector.js';
import { ModelSelectorComponent } from '../components/model-selector.js';
import { askModalQuestion } from '../modal-question.js';
import { promptForApiKeyIfNeeded } from '../prompt-api-key.js';
import type { SlashCommandContext } from './types.js';

/**
 * Key used to store the active browser settings in controller state.
 * This tracks what browser config is actually running in this instance,
 * which may differ from the settings file if another instance changed it.
 */
const ACTIVE_BROWSER_KEY = 'activeBrowserSettings';

type BrowserAgent = { browser?: MastraBrowser; setBrowser?: (browser?: MastraBrowser) => void };
type StorageStateExportBrowser = MastraBrowser & { exportStorageState: (path: string) => Promise<void> };

/**
 * /browser command - Configure browser automation for agents.
 *
 * Usage:
 *   /browser              - Interactive setup wizard
 *   /browser status       - Show current browser configuration
 *   /browser on           - Enable browser with current settings
 *   /browser off          - Disable browser
 *   /browser set <k> <v>  - Set a specific setting (profile, executablePath, storageState, cdpUrl, model, viewport)
 */

/**
 * Validate a `provider/model` id against the providers Stagehand can resolve.
 *
 * Stagehand splits the id on its first slash and throws during browser startup
 * if the prefix is not a provider it knows, so both halves are checked here
 * where the error can still be acted on.
 *
 * @returns an error message, or undefined when the id is usable.
 */
function stagehandModelError(modelId: string): string | undefined {
  const slashIndex = modelId.indexOf('/');
  if (slashIndex <= 0 || slashIndex === modelId.length - 1) {
    return `Invalid model: ${modelId}. Use <provider>/<model>, for example anthropic/claude-sonnet-4-5.`;
  }
  const provider = modelId.slice(0, slashIndex);
  if (!(STAGEHAND_MODEL_PROVIDERS as readonly string[]).includes(provider)) {
    return `Unsupported model provider: ${provider}. Supported providers: ${STAGEHAND_MODEL_PROVIDERS.join(', ')}.`;
  }
  return undefined;
}

/** Render a viewport for status output and confirmations. */
function formatViewport(viewport: BrowserViewport | undefined): string {
  if (!viewport) return 'default';
  if (viewport === 'window') return 'window (match browser window)';
  return `${viewport.width}x${viewport.height}`;
}

/**
 * `'window'` skips viewport emulation, which only works where the provider can
 * be told not to emulate. Stagehand overwrites an absent viewport with its own
 * default when it launches the browser itself, so it can only honor `'window'`
 * when connecting to an already-running browser over CDP.
 */
function windowViewportError(browser: BrowserSettings): string | undefined {
  if (browser.provider !== 'stagehand' || browser.cdpUrl) return undefined;
  return 'viewport window is not supported when stagehand launches its own browser. Use the agent-browser provider, set a cdpUrl, or pick explicit dimensions.';
}

/** Preset choices offered by the `/browser set viewport` picker. */
function viewportChoices(browser: BrowserSettings): Array<{ label: string; description?: string }> {
  const choices = Object.entries(VIEWPORT_PRESETS).map(([name, size]) => ({
    label: name,
    description: `${size.width}x${size.height}`,
  }));
  if (!windowViewportError(browser)) {
    choices.push({ label: 'window', description: 'Follow the real browser window (no emulation)' });
  }
  choices.push({ label: 'custom', description: 'Enter WIDTHxHEIGHT, e.g. 1600x1000' });
  return choices;
}

/**
 * Persist a viewport choice. Kept separate so the picker and the
 * `/browser set viewport <value>` path apply the same validation.
 */
function applyViewport(ctx: SlashCommandContext, settings: GlobalSettings, viewport: BrowserViewport): boolean {
  if (viewport === 'window') {
    const error = windowViewportError(settings.browser);
    if (error) {
      ctx.showError(error);
      return false;
    }
  }
  settings.browser.viewport = viewport;
  saveSettings(settings);
  ctx.showInfo(`Set viewport = ${formatViewport(viewport)}\nRun /browser on to apply.`);
  return true;
}

/**
 * Ask for a viewport from a preset list rather than making the user recall
 * dimensions, falling back to a free-text prompt for a custom size.
 */
async function promptForViewport(ctx: SlashCommandContext, settings: GlobalSettings): Promise<void> {
  const choice = await askInline(ctx, 'Viewport size?', viewportChoices(settings.browser));
  if (!choice) return;

  if (choice === 'custom') {
    const custom = await askText(ctx, 'Viewport (WIDTHxHEIGHT):', formatViewport(settings.browser.viewport));
    if (!custom) return;
    const parsed = parseViewportInput(custom);
    if (!parsed) {
      ctx.showError(`Invalid viewport: ${custom}. Use WIDTHxHEIGHT, for example 1280x720.`);
      return;
    }
    applyViewport(ctx, settings, parsed);
    return;
  }

  const parsed = parseViewportInput(choice);
  if (!parsed) {
    ctx.showError(`Invalid viewport: ${choice}.`);
    return;
  }
  applyViewport(ctx, settings, parsed);
}

/**
 * Open the shared model picker, restricted to providers Stagehand can resolve,
 * and persist the choice as the browser model.
 */
async function promptForStagehandModel(
  ctx: SlashCommandContext,
  settings: GlobalSettings,
  browser: BrowserSettings,
): Promise<void> {
  if (browser.provider !== 'stagehand') {
    ctx.showError('model is only supported by the stagehand provider.');
    return;
  }

  const supported = new Set<string>(STAGEHAND_MODEL_PROVIDERS);
  const models = (await ctx.state.controller.listAvailableModels()).filter(m => supported.has(m.provider));

  if (models.length === 0) {
    ctx.showError(
      `No models available for Stagehand. Supported providers: ${STAGEHAND_MODEL_PROVIDERS.join(', ')}.\n` +
        `You can also set one directly: /browser set model anthropic/claude-sonnet-4-5`,
    );
    return;
  }

  await new Promise<void>(resolve => {
    const selector = new ModelSelectorComponent({
      tui: ctx.state.ui,
      models,
      currentModelId: browser.stagehand?.model,
      title: 'Select Browser Model',
      onSelect: async (model: ModelItem) => {
        ctx.state.ui.hideOverlay();
        // The picker also accepts a freely typed id, which bypasses the
        // filtered list above.
        const error = stagehandModelError(model.id);
        if (error) {
          ctx.showError(error);
          resolve();
          return;
        }
        await promptForApiKeyIfNeeded(ctx.state.ui, model, ctx.authStorage);
        settings.browser.stagehand = {
          ...settings.browser.stagehand,
          env: settings.browser.stagehand?.env ?? 'LOCAL',
          model: model.id,
        };
        saveSettings(settings);
        ctx.showInfo(`Set model = ${model.id}\nRun /browser on to apply.`);
        resolve();
      },
      onCancel: () => {
        ctx.state.ui.hideOverlay();
        ctx.showInfo('Model selection cancelled.');
        resolve();
      },
    });
    ctx.state.ui.showOverlay(selector, { width: '60%' });
  });
}

/**
 * Helper to show an inline question and return the answer.
 */
function askInline(
  ctx: SlashCommandContext,
  question: string,
  options: Array<{ label: string; description?: string }>,
): Promise<string | null> {
  return askModalQuestion(ctx.state.ui, { question, options });
}

/**
 * Helper to show an inline text input and return the answer.
 */
async function askText(ctx: SlashCommandContext, question: string, defaultValue?: string): Promise<string | null> {
  const answer = await askModalQuestion(ctx.state.ui, { question, defaultValue, allowEmptyInput: true });
  const trimmed = answer?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Check for provider mismatch in profile and prompt for confirmation.
 * Returns true if we should proceed, false if user cancelled.
 */
async function checkAndConfirmProviderMismatch(
  ctx: SlashCommandContext,
  profile: string | undefined,
  targetProvider: BrowserProvider,
): Promise<boolean> {
  if (!profile) return true;

  const existingProvider = checkProfileProviderMismatch(profile, targetProvider);
  if (!existingProvider) return true;

  const targetLabel = targetProvider === 'stagehand' ? 'Stagehand' : 'AgentBrowser';
  const existingLabel = existingProvider === 'stagehand' ? 'Stagehand' : 'AgentBrowser';

  ctx.showInfo(
    `⚠️  Warning: This profile was last used by ${existingLabel}, but you're now using ${targetLabel}.\n` +
      'Using the same profile across different providers can cause compatibility issues.',
  );

  const proceed = await askInline(ctx, 'Continue anyway?', [
    { label: 'No', description: 'Cancel and use a different profile' },
    { label: 'Yes', description: 'Proceed (may cause issues)' },
  ]);

  return proceed === 'Yes';
}

/**
 * Apply browser settings to all mode agents and track the active settings.
 */
function resolveModeAgent(mode: unknown, agentControllerState: unknown): BrowserAgent | undefined {
  const modeAgent = (mode as { agent?: unknown }).agent;
  return typeof modeAgent === 'function'
    ? (modeAgent(agentControllerState) as BrowserAgent)
    : (modeAgent as BrowserAgent | undefined);
}

function applyBrowserToAgents(
  ctx: SlashCommandContext,
  browser: MastraBrowser | undefined,
  browserSettings?: BrowserSettings,
): void {
  const modes = ctx.controller.listModes();
  let agentControllerState: unknown;
  for (const mode of modes) {
    const agent = resolveModeAgent(mode, (agentControllerState ??= ctx.state.session.state.get()));
    agent?.setBrowser?.(browser);
  }
  ctx.controller.setBrowser?.(browser);
  // Track the active browser settings in controller state
  void ctx.state.session.state.set({ [ACTIVE_BROWSER_KEY]: browserSettings } as any);
}

/**
 * Get a summary key for browser settings to detect config drift.
 */
function getBrowserConfigKey(settings: BrowserSettings): string {
  if (!settings.enabled) return 'disabled';
  const parts: string[] = [settings.provider];
  if (settings.provider === 'stagehand' && settings.stagehand?.env) {
    parts.push(settings.stagehand.env);
  }
  if (settings.provider === 'stagehand' && settings.stagehand?.model) {
    parts.push(`model:${settings.stagehand.model}`);
  }
  parts.push(settings.headless ? 'headless' : 'headed');
  if (settings.viewport) parts.push(`viewport:${formatViewport(settings.viewport)}`);
  if (settings.profile) parts.push(`profile:${settings.profile}`);
  if (settings.executablePath) parts.push(`exec:${settings.executablePath}`);
  if (settings.cdpUrl) parts.push(`cdp:${settings.cdpUrl}`);
  if (settings.agentBrowser?.storageState) parts.push(`storage:${settings.agentBrowser.storageState}`);
  return parts.join(':');
}

/**
 * /browser — Configure browser automation settings.
 *
 * Interactive flow to set up browser provider (Stagehand or AgentBrowser),
 * headless mode, and provider-specific options.
 *
 * Changes are applied immediately to the current session.
 */
export async function handleBrowserCommand(ctx: SlashCommandContext, args: string[] = []): Promise<void> {
  const settings = loadSettings();
  const browser = settings.browser;

  // Handle quick commands
  const arg = args[0]?.toLowerCase();

  // /browser set <key> <value> - set a specific setting
  if (arg === 'set') {
    const key = args[1]?.toLowerCase();
    const value = args.slice(2).join(' '); // Allow spaces in paths

    if (!key) {
      ctx.showInfo(
        'Usage: /browser set <key> <value>\n\n' +
          'Keys:\n' +
          '  profile <path>       - Browser profile directory\n' +
          '  executablePath <path> - Browser executable path\n' +
          '  storageState <path>  - Playwright storage state file (agent-browser only)\n' +
          '  cdpUrl <url>         - CDP WebSocket URL\n' +
          '  model [provider/id]  - Model Stagehand uses for AI operations (stagehand only).\n' +
          '                         Omit the value to pick from a list.\n' +
          '  viewport [size]      - Preset name, WIDTHxHEIGHT, or window.\n' +
          '                         Omit the value to pick from a list.\n\n' +
          'To remove a setting, use: /browser clear <key>\n\n' +
          'Examples:\n' +
          '  /browser set profile ~/.mastracode/browser-profile-stagehand\n' +
          '  /browser set model\n' +
          '  /browser set viewport 1920x1080\n' +
          '  /browser set viewport window\n' +
          '  /browser set model anthropic/claude-sonnet-4-5\n' +
          '  /browser set executablePath /Applications/Google Chrome.app/Contents/MacOS/Google Chrome',
      );
      return;
    }

    const validKeys = ['profile', 'executablepath', 'storagestate', 'cdpurl', 'model', 'viewport'];
    if (!validKeys.includes(key)) {
      ctx.showError(
        `Unknown key: ${args[1]}. Valid keys: profile, executablePath, storageState, cdpUrl, model, viewport`,
      );
      return;
    }

    if (!value) {
      // Model ids are hard to recall, so an omitted value opens the same
      // searchable picker used elsewhere instead of erroring out.
      if (key === 'model') {
        await promptForStagehandModel(ctx, settings, browser);
        return;
      }
      if (key === 'viewport') {
        await promptForViewport(ctx, settings);
        return;
      }
      ctx.showError(
        `Missing value. Use: /browser set ${args[1]} <value>\nTo remove a setting, use: /browser clear ${args[1]}`,
      );
      return;
    }

    const expandedValue = value.trim().replace(/^~/, process.env.HOME || '~');

    switch (key) {
      case 'profile':
        settings.browser.profile = expandedValue;
        // Auto-set preserveUserDataDir for Stagehand when profile is configured
        settings.browser.stagehand = {
          ...settings.browser.stagehand,
          env: settings.browser.stagehand?.env ?? 'LOCAL',
          preserveUserDataDir: true,
        };
        // Profile is a launch option — incompatible with CDP connection
        if (settings.browser.cdpUrl) {
          delete settings.browser.cdpUrl;
          ctx.showInfo(`Note: Cleared cdpUrl (incompatible with profile).`);
        }
        break;
      case 'executablepath':
        settings.browser.executablePath = expandedValue;
        // ExecutablePath is a launch option — incompatible with CDP connection
        if (settings.browser.cdpUrl) {
          delete settings.browser.cdpUrl;
          ctx.showInfo(`Note: Cleared cdpUrl (incompatible with executablePath).`);
        }
        break;
      case 'storagestate':
        if (browser.provider !== 'agent-browser') {
          ctx.showError('storageState is only supported by agent-browser provider.');
          return;
        }
        settings.browser.agentBrowser = {
          ...settings.browser.agentBrowser,
          storageState: expandedValue,
        };
        break;
      case 'model': {
        if (browser.provider !== 'stagehand') {
          ctx.showError('model is only supported by the stagehand provider.');
          return;
        }
        const modelId = value.trim();
        const modelError = stagehandModelError(modelId);
        if (modelError) {
          ctx.showError(modelError);
          return;
        }
        settings.browser.stagehand = {
          ...settings.browser.stagehand,
          env: settings.browser.stagehand?.env ?? 'LOCAL',
          model: modelId,
        };
        saveSettings(settings);
        ctx.showInfo(`Set model = ${modelId}\nRun /browser on to apply.`);
        return;
      }
      case 'viewport': {
        const parsed = parseViewportInput(value);
        if (!parsed) {
          ctx.showError(
            `Invalid viewport: ${value.trim()}. Use WIDTHxHEIGHT, window, or a preset (${Object.keys(VIEWPORT_PRESETS).join(', ')}).`,
          );
          return;
        }
        applyViewport(ctx, settings, parsed);
        return;
      }
      case 'cdpurl':
        settings.browser.cdpUrl = expandedValue;
        // CDP connects to an existing browser — launch options are ignored
        if (settings.browser.profile || settings.browser.executablePath) {
          delete settings.browser.profile;
          delete settings.browser.executablePath;
          if (settings.browser.stagehand) {
            delete settings.browser.stagehand.preserveUserDataDir;
          }
          ctx.showInfo(`Note: Cleared profile and executablePath (ignored when using cdpUrl).`);
        }
        if (settings.browser.agentBrowser?.storageState) {
          delete settings.browser.agentBrowser.storageState;
        }
        break;
    }

    saveSettings(settings);
    ctx.showInfo(`Set ${args[1]} = ${expandedValue}\nRun /browser on to apply.`);
    return;
  }

  if (arg === 'status') {
    // Get the active browser settings from controller state (what's actually running)
    const state = ctx.state.session.state.get() as any;
    const activeSettings = state?.[ACTIVE_BROWSER_KEY] as BrowserSettings | undefined;

    // Check for config drift between file and active instance
    const hasDrift = activeSettings && getBrowserConfigKey(browser) !== getBrowserConfigKey(activeSettings);

    if (hasDrift && activeSettings) {
      // Show both active and file settings when they differ
      const lines: string[] = [];

      // Active session settings
      const activeProvider =
        activeSettings.provider === 'stagehand' ? 'Stagehand (AI-powered)' : 'AgentBrowser (deterministic)';
      const activeIsBrowserbase =
        activeSettings.provider === 'stagehand' && activeSettings.stagehand?.env === 'BROWSERBASE';
      lines.push('Browser (active):');
      lines.push(`  Provider: ${activeProvider}`);
      if (activeSettings.provider === 'stagehand' && activeSettings.stagehand) {
        lines.push(`  Environment: ${activeSettings.stagehand.env}`);
        if (activeSettings.stagehand.model) lines.push(`  Model: ${activeSettings.stagehand.model}`);
      }
      if (!activeIsBrowserbase) {
        lines.push(`  Headless: ${activeSettings.headless ? 'yes' : 'no'}`);
      }
      lines.push(`  Viewport: ${formatViewport(activeSettings.viewport)}`);
      if (activeSettings.executablePath) lines.push(`  Executable: ${activeSettings.executablePath}`);
      if (activeSettings.profile) lines.push(`  Profile: ${activeSettings.profile}`);
      if (activeSettings.agentBrowser?.storageState)
        lines.push(`  Storage State: ${activeSettings.agentBrowser.storageState}`);
      if (activeSettings.cdpUrl) lines.push(`  CDP URL: ${activeSettings.cdpUrl}`);

      lines.push('');

      // Pending changes from file
      const fileProvider = browser.provider === 'stagehand' ? 'Stagehand (AI-powered)' : 'AgentBrowser (deterministic)';
      const fileIsBrowserbase = browser.provider === 'stagehand' && browser.stagehand?.env === 'BROWSERBASE';
      lines.push('Pending changes (not yet applied):');
      lines.push(`  Provider: ${fileProvider}`);
      if (browser.provider === 'stagehand' && browser.stagehand) {
        lines.push(`  Environment: ${browser.stagehand.env}`);
        if (browser.stagehand.model) lines.push(`  Model: ${browser.stagehand.model}`);
      }
      if (!fileIsBrowserbase) {
        lines.push(`  Headless: ${browser.headless ? 'yes' : 'no'}`);
      }
      lines.push(`  Viewport: ${formatViewport(browser.viewport)}`);
      if (browser.executablePath) lines.push(`  Executable: ${browser.executablePath}`);
      if (browser.profile) lines.push(`  Profile: ${browser.profile}`);
      if (browser.agentBrowser?.storageState) lines.push(`  Storage State: ${browser.agentBrowser.storageState}`);
      if (browser.cdpUrl) lines.push(`  CDP URL: ${browser.cdpUrl}`);

      lines.push('');
      lines.push('⚠️  /browser on to apply, /browser to reconfigure, or restart.');

      ctx.showInfo(lines.join('\n'));
    } else if (!browser.enabled) {
      ctx.showInfo('Browser: disabled');
    } else {
      // Normal status (no drift)
      const providerLabel =
        browser.provider === 'stagehand' ? 'Stagehand (AI-powered)' : 'AgentBrowser (deterministic)';
      const isBrowserbase = browser.provider === 'stagehand' && browser.stagehand?.env === 'BROWSERBASE';
      const lines = [`Browser: enabled`, `  Provider: ${providerLabel}`];
      if (browser.provider === 'stagehand' && browser.stagehand) {
        lines.push(`  Environment: ${browser.stagehand.env}`);
        if (browser.stagehand.model) lines.push(`  Model: ${browser.stagehand.model}`);
      }
      if (!isBrowserbase) {
        lines.push(`  Headless: ${browser.headless ? 'yes' : 'no'}`);
      }
      lines.push(`  Viewport: ${formatViewport(browser.viewport)}`);
      if (browser.executablePath) {
        lines.push(`  Executable: ${browser.executablePath}`);
      }
      if (browser.profile) {
        lines.push(`  Profile: ${browser.profile}`);
      }
      if (browser.agentBrowser?.storageState) {
        lines.push(`  Storage State: ${browser.agentBrowser.storageState}`);
      }
      if (browser.cdpUrl) {
        lines.push(`  CDP URL: ${browser.cdpUrl}`);
      }
      ctx.showInfo(lines.join('\n'));
    }
    return;
  }

  if (arg === 'off' || arg === 'disable') {
    const disabledSettings = { ...settings.browser, enabled: false };
    settings.browser = disabledSettings;
    saveSettings(settings);
    applyBrowserToAgents(ctx, undefined, disabledSettings);
    ctx.showInfo('Browser disabled.');
    return;
  }

  if (arg === 'on' || arg === 'enable') {
    const nextBrowser = { ...settings.browser, enabled: true };

    // Check for provider mismatch in profile
    const shouldProceed = await checkAndConfirmProviderMismatch(ctx, nextBrowser.profile, nextBrowser.provider);
    if (!shouldProceed) {
      ctx.showInfo('Browser enable cancelled.');
      return;
    }

    try {
      const browserInstance = await createBrowserFromSettings(nextBrowser);
      applyBrowserToAgents(ctx, browserInstance, nextBrowser);
      if (nextBrowser.profile && nextBrowser.provider) {
        setProfileProvider(nextBrowser.profile, nextBrowser.provider);
      }
      settings.browser = nextBrowser;
      saveSettings(settings);
      const providerLabel = browser.provider === 'stagehand' ? 'Stagehand' : 'AgentBrowser';
      ctx.showInfo(`Browser enabled (${providerLabel}).`);
    } catch (err) {
      ctx.showError(`Failed to enable browser: ${err instanceof Error ? err.message : String(err)}`);
    }
    return;
  }

  // /browser clear [field] - reset all or specific setting (preserves enabled state)
  if (arg === 'clear') {
    const field = args[1]?.toLowerCase();

    if (!field) {
      // Clear all - reset to defaults but preserve enabled state
      const wasEnabled = settings.browser.enabled;
      settings.browser = {
        enabled: wasEnabled,
        provider: 'stagehand',
        headless: false,
        viewport: { width: 1280, height: 720 },
      };
      saveSettings(settings);
      // If it was enabled, we need to recreate the browser with new settings
      if (wasEnabled) {
        try {
          const browserInstance = await createBrowserFromSettings(settings.browser);
          applyBrowserToAgents(ctx, browserInstance, settings.browser);
        } catch (err) {
          // If recreation fails, disable and report
          settings.browser.enabled = false;
          saveSettings(settings);
          applyBrowserToAgents(ctx, undefined);
          ctx.showError(
            `Browser settings reset, but failed to restart: ${err instanceof Error ? err.message : String(err)}`,
          );
          return;
        }
      } else {
        applyBrowserToAgents(ctx, undefined);
      }
      ctx.showInfo('Browser settings reset to defaults.');
      return;
    }

    // Clear specific field
    switch (field) {
      case 'profile':
        delete settings.browser.profile;
        if (settings.browser.stagehand) {
          delete settings.browser.stagehand.preserveUserDataDir;
        }
        break;
      case 'executablepath':
        delete settings.browser.executablePath;
        break;
      case 'storagestate':
        if (settings.browser.agentBrowser) {
          delete settings.browser.agentBrowser.storageState;
        }
        break;
      case 'cdpurl':
        delete settings.browser.cdpUrl;
        break;
      case 'model':
        if (settings.browser.stagehand) {
          delete settings.browser.stagehand.model;
        }
        break;
      case 'viewport':
        settings.browser.viewport = { ...VIEWPORT_PRESETS.desktop };
        break;
      default:
        ctx.showError(
          `Unknown field: ${field}. Valid fields: profile, executablePath, storageState, cdpUrl, model, viewport`,
        );
        return;
    }

    saveSettings(settings);
    ctx.showInfo(`Cleared ${field}. Run /browser on to apply.`);
    return;
  }

  // /browser export storageState <path> - export current session's storage state
  if (arg === 'export') {
    const what = args[1]?.toLowerCase();
    const exportPath = args.slice(2).join(' ').trim();

    if (what !== 'storagestate' && what !== 'storage-state') {
      ctx.showError('Usage: /browser export storageState <path>');
      return;
    }

    if (!exportPath) {
      ctx.showError('Missing path. Usage: /browser export storageState <path>');
      return;
    }

    if (browser.provider !== 'agent-browser') {
      ctx.showError('export storageState is only supported by agent-browser provider.');
      return;
    }

    const currentMode = ctx.state.session.mode.resolve();
    const currentAgent = resolveModeAgent(currentMode, ctx.state.session.state.get());
    let browserInstance = currentAgent?.browser;

    if (!browserInstance && browser.enabled) {
      browserInstance = await createBrowserFromSettings(browser);
      applyBrowserToAgents(ctx, browserInstance, browser);
    }

    if (!browserInstance) {
      ctx.showError('Browser not enabled. Run /browser on first.');
      return;
    }

    const { AgentBrowser } = await import('@mastra/agent-browser');
    if (!(browserInstance instanceof AgentBrowser)) {
      ctx.showError('Current browser instance does not support exporting storage state.');
      return;
    }
    const exportableBrowser = browserInstance as StorageStateExportBrowser;

    const expandedPath = exportPath.replace(/^~/, process.env.HOME || '~');

    try {
      await exportableBrowser.exportStorageState(expandedPath);
      ctx.showInfo(`Storage state exported to: ${expandedPath}`);
    } catch (error) {
      ctx.showError(`Failed to export storage state: ${error instanceof Error ? error.message : String(error)}`);
    }
    return;
  }

  // /browser help, --help, -h, or unrecognized command
  if (arg && !['set', 'status', 'on', 'off', 'enable', 'disable', 'export'].includes(arg)) {
    const help = [
      'usage: /browser <command> [options]',
      '',
      '  (no command)   Interactive setup wizard',
      '  on, enable     Enable browser with current settings',
      '  off, disable   Disable browser',
      '  status         Show current configuration',
      '  clear          Reset all settings to defaults',
      '  clear <key>    Clear: profile, executablePath, storageState, cdpUrl, model, viewport',
      '  set <key> <v>  Set: profile, executablePath, storageState, cdpUrl, model, viewport',
      '  export storageState <path>  Export session cookies/localStorage (agent-browser)',
    ];
    ctx.showInfo(help.join('\n'));
    return;
  }

  // Step 1: Enable/disable browser (interactive)
  const enableChoice = await askInline(ctx, 'Enable browser automation?', [
    { label: 'Yes', description: 'Give the agent browser tools for web automation' },
    { label: 'No', description: 'Disable browser automation' },
  ]);

  // Cancel preserves current state
  if (!enableChoice) {
    ctx.showInfo('Browser setup cancelled.');
    return;
  }

  if (enableChoice === 'No') {
    if (browser.enabled) {
      settings.browser.enabled = false;
      saveSettings(settings);
      applyBrowserToAgents(ctx, undefined);
      ctx.showInfo('Browser automation disabled.');
    } else {
      ctx.showInfo('Browser automation remains disabled.');
    }
    return;
  }

  // Step 2: Select provider
  const providerChoice = await askInline(ctx, 'Select browser provider:', [
    { label: 'Stagehand', description: 'AI-powered (natural language instructions, recommended)' },
    { label: 'AgentBrowser', description: 'Deterministic (explicit selectors, requires Playwright)' },
  ]);

  if (!providerChoice) {
    ctx.showInfo('Browser setup cancelled.');
    return;
  }

  const provider: BrowserProvider = providerChoice === 'AgentBrowser' ? 'agent-browser' : 'stagehand';

  // Step 3: Stagehand-specific settings (ask environment first)
  let stagehandSettings: BrowserSettings['stagehand'];
  let isBrowserbase = false;
  if (provider === 'stagehand') {
    const envChoice = await askInline(ctx, 'Stagehand environment:', [
      { label: 'LOCAL', description: 'Run browser locally' },
      { label: 'BROWSERBASE', description: 'Use Browserbase cloud (requires API key)' },
    ]);

    if (!envChoice) {
      ctx.showInfo('Browser setup cancelled.');
      return;
    }

    const env = envChoice as StagehandEnv;
    isBrowserbase = env === 'BROWSERBASE';

    if (isBrowserbase) {
      ctx.showInfo(
        'Browserbase requires BROWSERBASE_API_KEY and BROWSERBASE_PROJECT_ID.\n' +
          'Set these in your shell profile (~/.zshrc) or pass them when starting MastraCode.',
      );
    }

    stagehandSettings = { env };
  }

  // Step 4: Headless mode (skip for Browserbase - runs in cloud)
  let headless = false;
  if (!isBrowserbase) {
    const headlessChoice = await askInline(ctx, 'Run in headless mode?', [
      { label: 'No', description: 'Show browser window (easier to debug)' },
      { label: 'Yes', description: 'Hide browser window (faster, less resource usage)' },
    ]);

    if (!headlessChoice) {
      ctx.showInfo('Browser setup cancelled.');
      return;
    }

    headless = headlessChoice === 'Yes';
  }

  // Step 5: Launch mode (bundled, custom executable, or CDP)
  let profile = browser.profile;
  let executablePath = browser.executablePath;
  let storageState = browser.agentBrowser?.storageState;
  let cdpUrl = browser.cdpUrl;

  if (isBrowserbase) {
    cdpUrl = undefined;
    profile = undefined;
    executablePath = undefined;
    storageState = undefined;
  }

  // Only show launch mode options for local browsers (not Browserbase)
  if (!isBrowserbase) {
    const launchMode = await askInline(ctx, 'How do you want to launch the browser?', [
      { label: 'Bundled browser', description: 'Use built-in Chromium (recommended)' },
      { label: 'Custom executable', description: 'Use Chrome, Brave, Edge, etc.' },
      { label: 'Connect via CDP', description: 'Connect to an already-running browser' },
    ]);

    if (!launchMode) {
      ctx.showInfo('Browser setup cancelled.');
      return;
    }

    if (launchMode === 'Custom executable') {
      // Clear cdpUrl when using custom browser (mutually exclusive)
      cdpUrl = undefined;

      const execPath = await askText(ctx, 'Browser executable path:', executablePath);
      if (execPath === null) {
        ctx.showInfo('Browser setup cancelled.');
        return;
      }
      executablePath = execPath.replace(/^~/, process.env.HOME || '~');
    } else if (launchMode === 'Connect via CDP') {
      const cdpUrlInput = await askText(ctx, 'CDP WebSocket URL (e.g., ws://localhost:9222):', cdpUrl);
      if (cdpUrlInput === null) {
        ctx.showInfo('Browser setup cancelled.');
        return;
      }
      cdpUrl = cdpUrlInput;
      // Clear launch options when using CDP (they don't apply)
      profile = undefined;
      executablePath = undefined;
      storageState = undefined;
    } else {
      // Bundled browser - clear custom paths
      cdpUrl = undefined;
      executablePath = undefined;
    }

    // Step 6: Profile option (only for bundled or custom executable, not CDP)
    if (launchMode !== 'Connect via CDP') {
      const useProfile = await askInline(ctx, 'Use a browser profile?', [
        { label: 'No', description: 'Fresh session each time' },
        { label: 'Yes', description: 'Persist logins, cookies, extensions' },
      ]);

      if (!useProfile) {
        ctx.showInfo('Browser setup cancelled.');
        return;
      }

      if (useProfile === 'Yes') {
        const defaultProfile = `~/.mastracode/browser-profile-${provider}`;
        const profilePath = await askText(ctx, 'Profile directory path:', profile || defaultProfile);
        if (profilePath === null) {
          ctx.showInfo('Browser setup cancelled.');
          return;
        }
        profile = profilePath.replace(/^~/, process.env.HOME || '~');
      } else {
        profile = undefined;
      }
    }
  }

  // Build new browser settings
  // Auto-set preserveUserDataDir when profile is configured for Stagehand
  if (provider === 'stagehand' && profile && stagehandSettings) {
    stagehandSettings.preserveUserDataDir = true;
  }

  const nextBrowser: BrowserSettings = {
    enabled: true,
    provider,
    headless,
    viewport: browser.viewport ?? { width: 1280, height: 720 },
    cdpUrl,
    profile,
    executablePath,
    stagehand: stagehandSettings,
    agentBrowser: storageState ? { storageState } : undefined,
  };

  // Check for provider mismatch in profile
  const shouldProceed = await checkAndConfirmProviderMismatch(ctx, profile, provider);
  if (!shouldProceed) {
    ctx.showInfo('Browser setup cancelled.');
    return;
  }

  // Apply browser to agents first, then persist on success
  try {
    const browserInstance = await createBrowserFromSettings(nextBrowser);
    applyBrowserToAgents(ctx, browserInstance, nextBrowser);
    if (nextBrowser.profile && nextBrowser.provider) {
      setProfileProvider(nextBrowser.profile, nextBrowser.provider);
    }
    settings.browser = nextBrowser;
    saveSettings(settings);
  } catch (err) {
    ctx.showError(`Failed to create browser: ${err instanceof Error ? err.message : String(err)}`);
    return;
  }

  // Summary
  const summary = [
    'Browser automation enabled:',
    `  Provider: ${provider === 'stagehand' ? 'Stagehand (AI-powered)' : 'AgentBrowser (deterministic)'}`,
  ];

  if (provider === 'stagehand' && stagehandSettings) {
    summary.push(`  Environment: ${stagehandSettings.env}`);
  }

  // Only show headless for local browsers
  if (!isBrowserbase) {
    summary.push(`  Headless: ${headless ? 'yes' : 'no'}`);
  }

  // Show advanced options if configured
  if (cdpUrl) {
    summary.push(`  CDP URL: ${cdpUrl}`);
  }
  if (executablePath) {
    summary.push(`  Executable: ${executablePath}`);
  }
  if (profile) {
    summary.push(`  Profile: ${profile}`);
  }
  if (storageState) {
    summary.push(`  Storage State: ${storageState}`);
  }

  ctx.showInfo(summary.join('\n'));
}
